'use client';

import { Calendar, LayoutDashboard, Search } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';

import type { MobileBottomNavItem } from '@ui/components/features/layout/mobileBottomNav';
import { MobileBottomNav } from '@ui/components/features/layout/mobileBottomNav';
import Home from '@ui/components/icons/Home';
import ThreeDCube from '@ui/components/icons/ThreeDCube';
import Ticket from '@ui/components/icons/Ticket';
import { ProfileNavIcon } from '@website/components/AppHeader/ProfileNavIcon';
import { useUserType } from '@website/hooks/useCurrentUser';
import { useHasMounted } from '@website/hooks/useHasMounted';
import { useUnreadCount } from '@website/hooks/useUnreadCount';
import { useUpcomingBookingsCount } from '@website/hooks/useUpcomingBookingsCount';
import { useUserCTAs } from '@website/hooks/useUserCTAs';

/**
 * Mobile bottom navigation component.
 * Reads user_type cookie synchronously via useUserType for zero-flash rendering.
 * No longer requires initialUserType prop from server.
 */
export const BottomNav = () => {
  const userType = useUserType();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations('Navigation');
  const hasMounted = useHasMounted();
  // Light polling so the unread-count cache stays fresh while the user
  // navigates the app. Shared with the header `NotificationMenu` cache.
  const { count: unreadCount } = useUnreadCount({ enablePolling: true });
  // Upcoming bookings count — shown as a badge on the bookings tab so users
  // know how many sessions they have ahead without opening the page.
  const { count: upcomingBookingsCount } = useUpcomingBookingsCount();
  // Mobile Settings tab badge composition:
  //   in-menu CTAs (mentor profile completion / verification, Stripe
  //   payments action, PWA install nudge) + unread notifications.
  //
  // `useUserCTAs().settingsCount` owns the visible-markers half — its
  // invariant is "settingsCount === markers visible inside the menu" and
  // that's what the desktop header dot reads (see AuthenticatedActions).
  //
  // Mobile is intentionally different: the bottom-nav Settings tab is the
  // most prominent persistent affordance on small screens and acts as the
  // consolidator for unread notifications too (the header bell is harder to
  // notice on mobile). Adding unread here only — not inside the hook —
  // keeps the hook's invariant clean while still surfacing notifications
  // where mobile users actually look for them.
  const { settingsCount } = useUserCTAs();
  const settingsBadge = settingsCount + unreadCount;

  // Don't render for public (non-authenticated) users
  // Also don't render before mount to prevent hydration mismatch
  if (!hasMounted || userType === 'public') {
    return null;
  }

  const isProfessional = userType === 'professional';

  // Different nav items for professionals vs regular users
  // All hrefs include locale prefix since MobileBottomNav uses plain next/link
  const authenticatedNavItems: MobileBottomNavItem[] = isProfessional
    ? [
        {
          label: t('dashboard'),
          href: `/${locale}/dashboard`,
          icon: <LayoutDashboard className="size-5" strokeWidth={1.5} />
        },
        {
          label: t('myActivity'),
          href: `/${locale}/activity`,
          icon: <ThreeDCube className="size-5" />
        },
        {
          label: t('myAgenda'),
          href: `/${locale}/agenda`,
          icon: <Calendar className="size-5" strokeWidth={1.5} />
        },
        {
          label: t('myBookings'),
          href: `/${locale}/bookings`,
          icon: <Ticket className="size-5" />,
          badge: upcomingBookingsCount,
          // Neutral-dark pill: upcoming bookings are ambient count info, not
          // an alert. Keeps red reserved for the settings/unread badge below.
          badgeVariant: 'count'
        },
        {
          label: t('settings'),
          href: `/${locale}/settings`,
          icon: <ProfileNavIcon className="size-5" />,
          badge: settingsBadge
        }
      ]
    : [
        {
          label: t('home'),
          href: `/${locale}/`,
          icon: <Home className="size-5" />
        },
        {
          label: t('search'),
          href: `/${locale}/search`,
          icon: <Search className="size-5" strokeWidth={1.5} />
        },
        {
          label: t('myBookings'),
          href: `/${locale}/bookings`,
          icon: <Ticket className="size-5" />,
          badge: upcomingBookingsCount,
          badgeVariant: 'count'
        },
        {
          label: t('settings'),
          href: `/${locale}/settings`,
          icon: <ProfileNavIcon className="size-5" />,
          badge: settingsBadge
        }
      ];

  // Strip locale prefix for proper path comparison
  const pathnameWithoutLocale = pathname.replace(/^\/(en|fr)/, '') || '/';

  // The Settings tab acts as the hub for the whole settings section, which
  // lives under `/settings/*`. Keep the tab active anywhere inside.
  const isUnderSettingsSection = (path: string) => path === '/settings' || path.startsWith('/settings/');

  const navItems = authenticatedNavItems.map((item) => {
    // Extract path without locale for comparison
    const itemPathWithoutLocale = item.href.replace(/^\/(en|fr)/, '') || '/';
    const isSettingsTab = itemPathWithoutLocale === '/settings';
    return {
      ...item,
      isActive: isSettingsTab
        ? isUnderSettingsSection(pathnameWithoutLocale)
        : itemPathWithoutLocale === '/'
          ? pathnameWithoutLocale === '/'
          : pathnameWithoutLocale === itemPathWithoutLocale ||
            pathnameWithoutLocale.startsWith(`${itemPathWithoutLocale}/`)
    };
  });

  return <MobileBottomNav navItems={navItems} />;
};
